<template>
	<div
		id="app"
		class="safe-area"
		:class="[locale === $i18n.EN ? 'language-en' : '', fullScreen ? 'fullscreen' : '']"
		:style="insetAppThemeVariables"
	>
		<router-view></router-view>

		<!-- gc：global-components gc_container-->
		<div v-if="showGcContainer" class="global-components-container">
			<!-- 第一类 水平垂直居中 -->
			<!-- 不带背景（遮罩层） -->
			<div v-if="showGcContent" class="global-components-content flex-main-cross-center" @touchmove.prevent>
				<!-- 提示语 -->
				<m-tooltip
					v-if="tooltip"
					:lang="$route.name === 'login'"
					:icon="tooltip_icon"
					:img="tooltip_img"
					:className="tooltip_className"
					:wrapClassName="tooltip_wrapclassName"
					:msg="tooltip_msg"
				></m-tooltip>
				<m-loading v-if="loading"></m-loading>
			</div>

			<!-- 带背景（遮罩层）-->
			<div
				v-if="showGcContentShade"
				class="global-components-content shade flex-main-cross-center shade-3"
				:class="{ 'shade-3': showDialog }"
			>
				<!-- 对话框 -->
				<m-dialog-box
					v-if="showDialog"
					:lang="$route.name === 'login'"
					:dialog_alert="dialog_alert"
					:dialog_affirm="dialog_affirm"
					:dialog_title="dialog_title"
					:dialog_text="dialog_text"
					:dialog_text_class="dialog_text_class"
					:cancel_text="dialog_cancel_text"
					:confirm_text="dialog_confirm_text"
					:alert_data="dialog_alert_data"
					:cancel_btn="dialog_cancel_btn"
					:confirm_btn="dialog_confirm_btn"
				></m-dialog-box>

				<!-- 验证码对话框 -->
				<m-auth-code-dialog
					v-if="auth_dialog"
					:auth_dialog="auth_dialog"
					:auth_dialog_type="auth_dialog_type"
					:auth_dialog_require="auth_dialog_require"
					:auth_dialog_content="auth_dialog_content"
					:auth_dialog_close__btn="auth_dialog_close__btn"
					:auth_dialog_success__btn="auth_dialog_success__btn"
				>
				</m-auth-code-dialog>
			</div>
		</div>

		<!-- 菜单栏 -->
		<m-menu v-if="$bridge.isH5 && $route.meta.menu"></m-menu>

		<EnvironmentFloat :current-environment="currentEnvironment" @switch="switchToStandardEnv" />
	</div>
</template>

<script>
import { isWx, getUserInfo, getUrlParam, setCookie } from './utils/api';
import { STATISTIC_NUMBER } from './utils/const';
import { rgbToHex, generateColorArray } from './utils/color';
import { mapActions, mapGetters, mapState } from 'vuex';
import { useEnvironment } from '@/hooks/useEnvironment';
import EnvironmentFloat from '@/components/environment-float.vue';

export default {
	name: 'app',
	components: {
		EnvironmentFloat
	},
	data() {
		return { isWx, fullScreen: false };
	},

	computed: {
		...mapGetters([
			'showDialog',
			'showGcContainer',
			'showGcContent',
			'showGcContentShade',
			'showGcContentShadeComposingNormal',
			'getIrThemeColor'
		]),

		...mapState([
			// 提示条 / 加载中 /
			'tooltip',
			'tooltip_msg',
			'tooltip_icon',
			'tooltip_img',
			'tooltip_className',
			'tooltip_wrapclassName',
			'loading',
			'agreement_box',

			// 对话框
			'dialog_alert',
			'dialog_affirm',
			'dialog_title',
			'dialog_text',
			'dialog_text_class',
			'dialog_cancel_text',
			'dialog_confirm_text',
			'dialog_cancel_btn',
			'dialog_alert_data',
			'dialog_cancel_btn_class',
			'dialog_confirm_btn',
			'dialog_confirm_btn_class',
			// 常规排版 - 名词解释
			'word_explain',
			'word_explain_data',
			// 验证码对话框
			'auth_dialog',
			'auth_dialog_type',
			'auth_dialog_require',
			'auth_dialog_content',
			'auth_dialog_close__btn',
			'auth_dialog_success__btn'
		]),
		locale() {
			return this.$i18n.locale;
		},
		insetAppThemeVariables() {
			const irThemeColor = this.getIrThemeColor;
			const generateOpacityList = generateColorArray(irThemeColor);
			const transferOpacityVars = generateOpacityList.reduce((prev, curr, idx) => {
				const v = {
					[`--ir-theme-color-opacity-${idx + 1}`]: rgbToHex(...curr)
				};
				return { ...prev, ...v };
			}, {});
			return {
				// 企业号主题色
				'--ir-theme-color': irThemeColor,
				...transferOpacityVars
			};
		}
	},

	created() {
		let userInfo = getUserInfo();
		if (userInfo && window.__bl && typeof window.__bl.setConfig === 'function') {
			window.__bl.setConfig({
				uid: userInfo.uid,
				ignore: {
					// /^Script error\.?$/
					ignoreErrors: ['ResizeObserver loop limit exceeded', 'Script error.']
				}
			});
		}
	},

	mounted() {
		// 解决button active失效问题
		document.addEventListener('touchstart', () => {}, true);
		// 阻止双指放大
		document.addEventListener('gesturestart', function (event) {
			event.preventDefault();
		});

		const params = getUrlParam();
		if (params.isFrame) {
			// pc通过iframe引入
			document.body.classList.add('is-pc-iframe');
			document.documentElement.classList.add('is-pc-iframe');
		}

		// 切换环境
		this.changeJmEnvs();
	},

	methods: {
		...mapActions(['statistic', 'fetchMarketGetToken']),

		watchRoute() {
			const $r = this.$route;
			this.fullScreen = !!$r.meta.fullScreen;

			// window.scrollTo(0, 0);
			// 声明公共参数
			const common_param = {
				pagesource: $r.query.pagesource || null,
				pagesourceid: $r.query.pagesourceid || null
			};
			console.log('🤡 # handleIframeMessage', $r.name, $r.path);
			if ($r?.path?.includes('/enterprise')) {
				// 判断是否在iframe中，触发postMessage
				if (window.self !== window.top) {
					console.log('handleIframeMessage');
					window.parent.postMessage(
						{
							type: 'ROUTE_CHANGE',
							route: $r.name
						},
						'*'
					);
				}
				return;
			}

			switch ($r.name) {
				case 'login':
					this.statistic({
						param: {
							pagetype: 'weblogin',
							typeid: null,
							pagearea: 'load',
							...common_param
						}
					});
					break;
				case 'live_index':
					this.statistic({
						param: {
							pagetype: 'eventlisthome',
							typeid: null,
							pagearea: 'load',
							...common_param
						}
					});
					break;
				case 'article_home':
					this.statistic({
						param: {
							pagetype: 'article',
							typeid: $r.query.id,
							pagearea: 'load',
							areaid: $r.query.id,
							statisticNum: STATISTIC_NUMBER.ARTICLE_LOAD,
							...common_param
						}
					});
					break;
				case 'topic_home':
					this.statistic({
						param: {
							pagetype: 'topicdetail',
							typeid: $r.query.id,
							pagearea: 'load',
							areaid: $r.query.id,
							...common_param
						}
					});
					break;
				case 'stock_home':
					this.statistic({
						param: {
							pagetype: 'stock',
							typeid: $r.query.id,
							pagearea: 'load',
							areaid: $r.query.id,
							...common_param
						}
					});
					break;
				case 'vip_home':
					this.statistic({
						param: {
							pagetype: 'vipbuy',
							typeid: null,
							pagearea: 'load',
							...common_param
						}
					});
					break;
				case 'institute_zs_login_fail':
					this.statistic({
						param: {
							pagetype: 'zsloginfail',
							typeid: null,
							pagearea: 'load',
							...common_param
						}
					});
					break;
				case 'institute_zs_join_event_fail':
					this.statistic({
						param: {
							pagetype: 'zsjoinfail',
							typeid: null,
							pagearea: 'load',
							...common_param
						}
					});
					break;
				case 'roadshow_record':
					break;
				case 'roadshow_home':
					// 后端要求去除埋点
					break;
			}
		},
		changeJmEnvs() {
			this.$router.onReady(() => {
				this.handleEnvironment();
			});
		}
	},

	watch: {
		$route: {
			handler() {
				this.watchRoute();
			},
			deep: true,
			immediate: true
		}
	},

	setup() {
		// 预发环境
		const { currentEnvironment, handleEnvironment, switchToStandardEnv } = useEnvironment();
		return {
			currentEnvironment,
			handleEnvironment,
			switchToStandardEnv
		};
	}
};
</script>

<style lang="less">
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s ease-in-out;
}

.fade-enter, .fade-leave-to /* .fade-leave-active 在低于版本 2.1.8 中 */ {
	opacity: 0;
}

/*缩放样式兼容 */
.transform-adapt:before {
	-webkit-transform-origin: 0 0;
	-moz-transform-origin: 0 0;
	-ms-transform-origin: 0 0;
	-o-transform-origin: 0 0;
	transform-origin: 0 0;
	-webkit-transform: scale(0.5, 0.5);
	-ms-transform: scale(0.5, 0.5);
	-o-transform: scale(0.5, 0.5);
	transform: scale(0.5, 0.5);
}

/* 处理一些兼容性的less */

/*容器的属性*/
/*flex-direction*/
/*flex-wrap*/
/*flex-flow*/
/*justify-content*/
/*align-items*/
/*align-content*/

/* row：横向 */
/* column：纵向 */

/*main： main axis 主轴*/
/*cross： cross axis 纵轴*/

/* flex */
.flex {
	display: flex;
}

.flex-row {
	.flex;
	flex-direction: row;
}

.flex-column {
	.flex;
	flex-direction: column;
}

.flex-main-center {
	justify-content: center;
}

.flex-main-end {
	justify-content: flex-end;
}

.flex-main-space-between {
	justify-content: space-between;
}

.flex-main-space-around {
	justify-content: space-around;
}

.flex-cross-start {
	align-items: flex-start;
}

.flex-cross-end {
	align-items: flex-end;
}

.flex-cross-center {
	align-items: center;
}

.flex-cross-baseline {
	align-items: baseline;
}

/* align-content */
/* 定义了多根轴线的对齐方式。如果项目只有一根轴线，该属性不起作用。 */
.flex-cross-content-start {
	align-content: flex-start;
}

.flex-cross-content-end {
	align-content: flex-end;
}

.flex-cross-content-center {
	align-content: center;
}

.flex-cross-content-space-between {
	align-content: space-between;
}

.flex-cross-content-space-around {
	align-content: space-around;
}

/* flex 横向主轴（水平）居中  */
/* 可用text-align/margin 替代 */
.flex-row-main-center {
	.flex-row;
	.flex-main-center;
}

/* flex 横向 纵轴（垂直）居中 */
.flex-row-cross-center {
	.flex-row;
	.flex-cross-center;
}

/* flex 纵向 主轴（垂直）居中 */
.flex-column-main-center {
	.flex-column;
	.flex-main-center;
}

/* flex 纵向 主轴（水平）两端对齐 */
.flex-column-main-space-between {
	.flex-column;
	.flex-cross-content-space-between;
}

/* flex 纵向 纵轴（水平）居中  */
.flex-column-cross-center {
	.flex-column;
	.flex-cross-center;
}

/* flex 横向 主轴（水平）靠右对齐 */
.flex-row-main-end {
	.flex-row;
	.flex-main-end;
}

/* flex 横向 主轴（水平）两端对齐 */
.flex-row-main-space-between {
	.flex-row;
	.flex-main-space-between;
}

/* flex 横向 主轴（水平）均匀布局 */
.flex-row-main-space-around {
	.flex-row;
	.flex-main-space-around;
}

/* flex 横向 换行 */
.flex-row-wrap {
	-webkit-box-lines: multiple;
	-webkit-flex-wrap: wrap;
	-moz-flex-wrap: wrap;
	-ms-flex-wrap: wrap;
	-o-flex-wrap: wrap;
	flex-wrap: wrap;
}

/* flex 纵向 主轴（水平）靠右对齐 */
.flex-column-main-end {
	.flex-column;
	.flex-main-end;
}

/* flex 纵向 主轴（水平）两端对齐 */
.flex-column-main-space-between {
	.flex-column;
	.flex-main-space-between;
}

/* flex 纵向 主轴（水平）均匀布局 */
.flex-column-main-around {
	.flex-column;
	.flex-main-space-around;
}

/*flex 实现 水平和垂直 同时居中*/
.flex-main-cross-center {
	.flex-row-main-center;
	.flex-cross-center;
}

.flex1 {
	flex: 1;
}
</style>
